/**
 * The pipeline's data layer.
 *
 * One place that knows the query keys, the row-version plumbing and how a flat
 * list of briefs becomes the four sections of somebody's day. The screens below
 * it ask for "my work" and get exactly that; none of them assembles a URL or
 * remembers to send `If-Match`.
 *
 * Keys are all `["pipeline", ...]`, so one `invalidateQueries({ queryKey:
 * pipelineKey })` after a move refreshes the card, the page it was moved from
 * and the bell together - a transition changes all three and they must never
 * disagree about what stage a brief is in.
 */
import { createElement, lazy, Suspense, useCallback, useEffect, useMemo } from "react";
import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  isOverdue,
  type BriefComment,
  type BriefCommentReaction,
  type BriefDetail,
  type BriefDetailResponse,
  type BriefListResponse,
  type BriefCommentsResponse,
  type BriefSummary,
  isBackwardMove,
  nextStages,
  canTransition,
  hasDeliveryFolder,
  type BriefTransitionRequest,
  type PipelineStage,
  type PipelineViewer,
  type Role,
  type TransitionBrief,
  type TransitionRefusal,
} from "@scandi/shared";
import { api, ApiError, type BriefListParams } from "@/lib/api";
import { useFailureToast } from "@/hooks/use-failure-toast";
import { useSession } from "@/hooks/use-session";

/** The root every pipeline query hangs off, so one invalidation reaches them all. */
export const pipelineKey = ["pipeline"] as const;

/** The approval's live sheet steps, in its toast: loaded with the first approval, not with every page. */
const SheetSyncToast = lazy(() => import("@/features/pipeline/editor/sheet-sync").then((m) => ({ default: m.SheetSyncToast })));

export const pipelineKeys = {
  all: pipelineKey,
  briefs: (params: BriefListParams) => ["pipeline", "briefs", params] as const,
  brief: (ref: string) => ["pipeline", "brief", ref] as const,
  periodSummary: (periodIndex: number) => ["pipeline", "period-summary", periodIndex] as const,
  notifications: () => ["pipeline", "notifications"] as const,
  lookups: () => ["pipeline", "lookups"] as const,
};

/* ------------------------------------------------------------------- reading */

/*
 * The reads as option objects, shared by the hooks and by the prefetcher (`lib/route-prefetch.ts`), so a read
 * started ahead of the page lands under exactly the key the page asks for.
 */
/** The concepts overview's list: every concept, 200 a page. */
export const OVERVIEW_LIST = { scope: "all", limit: 200 } as const;
/** An editor's own list, by due date. */
export const MY_WORK_LIST = { sort: "due", limit: 200 } as const;
export const briefsQuery = (params: BriefListParams = {}) =>
  queryOptions({ queryKey: pipelineKeys.briefs(params), queryFn: () => api.pipeline.list(params), staleTime: 30 * 1000 });
export const allBriefsQuery = (params: BriefListParams = {}) =>
  infiniteQueryOptions({
    queryKey: [...pipelineKeys.briefs(params), "all"],
    queryFn: ({ pageParam }) => api.pipeline.list({ ...params, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: 0,
    getNextPageParam: (last: BriefListResponse, pages: BriefListResponse[]) =>
      last.nextCursor && pages.length < 50 ? last.nextCursor : undefined,
    staleTime: 30 * 1000,
  });
export const briefQuery = (ref: string) =>
  queryOptions({ queryKey: pipelineKeys.brief(ref), queryFn: () => api.pipeline.get(ref), staleTime: 15 * 1000, retry: false });
export const lookupsQuery = () =>
  queryOptions({ queryKey: pipelineKeys.lookups(), queryFn: () => api.pipeline.lookups(), staleTime: 5 * 60 * 1000 });

export function useBriefs(params: BriefListParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({ ...briefsQuery(params), enabled: options.enabled ?? true });
}

/**
 * Every brief that matches, fetched a page at a time until there are no more.
 *
 * The concepts overview is where past work is looked up, so it needs the whole
 * archive rather than its first page. Pages arrive in order and render as they
 * land; the cap only guards against a runaway loop, far above the ~2,300 rows
 * that exist.
 */
export function useAllBriefs(params: BriefListParams = {}) {
  const query = useInfiniteQuery(allBriefsQuery(params));
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.rows) ?? [], [query.data]);
  return { ...query, rows, loadingMore: hasNextPage || isFetchingNextPage };
}

/**
 * One brief.
 *
 * `live` is for a page somebody keeps open while somebody else works on the
 * same concept - the concept page, where the strategist waits for a delivery
 * and the editor waits for a folder. It re-reads every 15 seconds (the brief
 * chat's own pace) and on coming back to the tab, so the rail, the buttons and
 * the folder follow the other side's move instead of offering a move that has
 * already happened and turning the next save into a "somebody else saved"
 * refusal. Only that page's observer asks for it: the composer holds a form
 * over the same brief and must not have it re-read under the cursor.
 */
export function useBrief(ref: string | undefined, options: { live?: boolean } = {}) {
  return useQuery({
    ...briefQuery(ref ?? ""),
    enabled: Boolean(ref),
    // Spread rather than set to undefined: an explicit undefined would
    // override the client's "no focus refetch" default with react-query's own.
    ...(options.live ? { refetchInterval: 15 * 1000, refetchOnWindowFocus: true } : {}),
  });
}

/**
 * One pay period of concepts: approved, delivered and created, for the
 * overview's period picker. Under the pipeline key, so a move anywhere
 * refreshes it with everything else.
 */
export function useConceptPeriodSummary(periodIndex: number | null) {
  return useQuery({
    queryKey: pipelineKeys.periodSummary(periodIndex ?? 0),
    queryFn: () => api.pipeline.periodSummary(periodIndex!),
    enabled: periodIndex !== null,
    staleTime: 30 * 1000,
    placeholderData: (previous) => previous,
  });
}

/**
 * Everything still moving, in one request.
 *
 * `live` drops shipped and cancelled, `due` sorts by the date the work is
 * actually owed rather than by id, and the whole live pipeline is small enough
 * (16 rows today, a few hundred at worst) that paging it would cost a round
 * trip to save nothing.
 */
const MY_WORK_PARAMS: BriefListParams = { live: true, sort: "due", limit: 200 };

export function useMyWork() {
  return useBriefs(MY_WORK_PARAMS);
}

/* ------------------------------------------------------------------ sections */

export interface WorkSections {
  /** Waiting on the reader, most urgent kind first. */
  yourTurn: BriefSummary[];
  inProgress: BriefSummary[];
  /** With somebody else, and nothing to do until they answer. */
  waiting: BriefSummary[];
  /** Finished lately. Collapsed by default: it is a pat on the back, not a task. */
  approved: BriefSummary[];
  /** Live work that is past its due date, across every section. */
  overdue: number;
}

/** How long a finished brief keeps its place on the page. */
const APPROVED_DAYS = 14;

/**
 * Which pile a brief belongs in depends on who is looking.
 *
 * `in_review` is the editor's "nothing to do" and the strategist's "do this
 * now", and a shared grouping would be wrong for one of them. Rather than two
 * screens, the same four sections are filled through one role-aware map.
 *
 * An editor never holds a concept before its release (the server keeps
 * `draft` and `strategist_review` from them), and both reviews after delivery
 * are "with somebody else" from their chair. For the team, the two reviews
 * that bracket the editor's work are what waits on them; an admin also owns
 * the final review, which a strategist only waits on.
 */
function sectionsFor(role: Role | null): Record<"yourTurn" | "inProgress" | "waiting", PipelineStage[]> {
  if (role === "editor") {
    return {
      yourTurn: ["changes_requested", "assigned"],
      inProgress: ["in_progress"],
      waiting: ["in_review", "pending_review"],
    };
  }
  if (role === "admin") {
    return {
      yourTurn: ["pending_review", "in_review", "strategist_review"],
      inProgress: ["in_progress", "assigned"],
      waiting: ["changes_requested"],
    };
  }
  return {
    yourTurn: ["in_review", "strategist_review"],
    inProgress: ["in_progress", "assigned"],
    waiting: ["changes_requested", "pending_review"],
  };
}

export function groupWork(rows: BriefSummary[], role: Role | null, today: string): WorkSections {
  const map = sectionsFor(role);
  const pick = (stages: PipelineStage[]) =>
    stages.flatMap((stage) => rows.filter((row) => row.stage === stage));

  const cutoff = Date.now() - APPROVED_DAYS * 86_400_000;
  const approved = rows
    .filter((row) => row.stage === "approved" || row.stage === "shipped")
    .filter((row) => new Date(row.updatedAt).getTime() >= cutoff)
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));

  // The shared rule, not a copy of it: a concept nobody has handed over yet,
  // or one waiting on the admin, is not late on the editor's account.
  const overdue = rows.filter((row) => isOverdue(row, today)).length;

  return {
    yourTurn: pick(map.yourTurn),
    inProgress: pick(map.inProgress),
    waiting: pick(map.waiting),
    approved,
    overdue,
  };
}

/* ------------------------------------------------------------------- writing */

/** What each step back says once it is made: by what it undoes, then by where it lands. */
function backToastKey(from: PipelineStage, to: PipelineStage, role: Role | null) {
  if (from === "changes_requested") return "pipeline.toasts.revisionUndone" as const;
  if (from === "approved") return "pipeline.toasts.approvalUndone" as const;
  switch (to) {
    case "strategist_review":
      return "pipeline.toasts.strategist_review" as const;
    case "assigned":
      return "pipeline.editor.toasts.backToTodo" as const;
    case "in_review":
      // The strategist took it back to herself; an admin handed it back to her.
      return role === "admin" ? ("pipeline.toasts.backToReview" as const) : ("pipeline.toasts.backFromAdmin" as const);
    default:
      return "pipeline.editor.toasts.backToDoing" as const;
  }
}

/** What a move needs from the caller, on top of the brief the hook already holds. */
export interface TransitionArgs extends BriefTransitionRequest {
  /**
   * The version to send as `If-Match` instead of the brief's current one. A
   * dialog over a live page pins the version it opened on, so a move somebody
   * else made while the reader was typing is refused rather than built on.
   */
  rowVersion?: number | null;
}

/**
 * A write refused because the brief moved on since it was read. The copy on
 * screen is stale, so it is read again at once instead of at the next poll:
 * an open dialog can then say what changed while the refusal is still news.
 */
const isStale = (error: unknown) => error instanceof ApiError && error.code === "pipeline_conflict";

/**
 * A move refused because the brief is not where the screen says it is: stale
 * as above, or the move no longer exists from where the brief stands now. A
 * move sent without a version (from a review card that had none to send) meets
 * the machine's refusal rather than the version check, and is just as much
 * news that the screen is behind.
 */
const isOutrun = (error: unknown) =>
  isStale(error) || (error instanceof ApiError && error.code === "pipeline_forbidden_transition");

/**
 * Move one brief.
 *
 * The row version travels as `If-Match` from whatever the caller last read, so
 * two people acting on the same brief at once end in a refusal rather than in a
 * lost note. On success the whole pipeline is invalidated: the card, the page
 * and the bell all changed.
 */
export function useTransitionBrief(
  brief: { id: number; rowVersion?: number | null },
  options: {
    /**
     * Said instead of the general refusal when the move is refused because the
     * brief moved on (`isOutrun`). An open note dialog passes its own: the
     * general "reload to see their version" would have the reader throw away
     * the very note the dialog is keeping for them.
     */
    outrunMessage?: string;
  } = {},
) {
  const { t } = useTranslation();
  const { role } = useSession();
  const onError = useFailureToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ rowVersion, ...body }: TransitionArgs) =>
      api.pipeline.transition(brief.id, body, rowVersion === undefined ? (brief.rowVersion ?? null) : rowVersion),
    // The move shows at once: the cached lists and the brief take the new stage before the server answers, and
    // go back to what they were if it refuses.
    onMutate: async ({ to }) => moveInCache(queryClient, brief.id, to),
    onSuccess: (result) => {
      if (result.brief) {
        // The card as it now stands, from the answer: the lists are put right without reading them again, and
        // everything else under the pipeline (the brief's own moves, counts, notifications) is read again.
        placeInLists(queryClient, result.brief);
        void queryClient.invalidateQueries({ queryKey: pipelineKey, predicate: (query) => !isBriefList(query.queryKey) });
      } else {
        void queryClient.invalidateQueries({ queryKey: pipelineKey });
      }
      // A step back is not the move its destination usually means: "ready for
      // editors" is what a strategist reads after releasing a concept, not what
      // an editor should read after un-starting it.
      if (isBackwardMove(result.from, result.to)) {
        toast.success(t(backToastKey(result.from, result.to, role), { ref: result.ref }));
        return;
      }
      if (result.to === "changes_requested" && result.revisionRound > 0) {
        toast.success(t("pipeline.toasts.changesRequestedRound", { ref: result.ref, round: result.revisionRound }));
        return;
      }
      // An admin's approval adds the hook files to the creatives sheet; the
      // toast carries the steps, live, wherever the approval was made.
      // Sent to the final review while it is switched off: approved on the spot.
      if (result.autoApproved && role !== "admin") {
        toast.success(t("pipeline.toasts.autoApproved", { ref: result.ref }));
        return;
      }
      if (result.to === "approved" && role === "admin") {
        toast.success(t("pipeline.toasts.approved", { ref: result.ref }), {
          description: createElement(Suspense, { fallback: null }, createElement(SheetSyncToast, { briefId: brief.id })),
          duration: 15_000,
        });
        return;
      }
      toast.success(t(`pipeline.toasts.${result.to}` as const, { ref: result.ref }));
    },
    onError: (error, _args, snapshot) => {
      snapshot?.restore();
      const outrun = isOutrun(error);
      if (outrun && options.outrunMessage) toast.error(options.outrunMessage);
      else onError(error);
      if (outrun) void queryClient.invalidateQueries({ queryKey: pipelineKey });
    },
  });
}

/** A briefs list's key: `["pipeline", "briefs", params]`, or the overview's `[..., "all"]`. */
function isBriefList(key: readonly unknown[]): boolean {
  return key[0] === "pipeline" && key[1] === "briefs";
}

type ListData = BriefListResponse | { pages: BriefListResponse[]; pageParams: unknown[] };

function mapRows(data: ListData | undefined, change: (row: BriefSummary) => BriefSummary): ListData | undefined {
  if (!data) return data;
  const page = (list: BriefListResponse): BriefListResponse => {
    let changed = false;
    const rows = list.rows.map((row) => {
      const next = change(row);
      if (next !== row) changed = true;
      return next;
    });
    return changed ? { ...list, rows } : list;
  };
  return "pages" in data ? { ...data, pages: data.pages.map(page) } : page(data);
}

/**
 * Write a stage move into every cached list and brief that shows the concept. Answers how to undo it. Reads in
 * flight are stopped first, so an answer from before the move cannot land on top of it.
 */
async function moveInCache(queryClient: QueryClient, briefId: number, to: PipelineStage): Promise<{ restore: () => void }> {
  await queryClient.cancelQueries({ predicate: (query) => isBriefList(query.queryKey) || (query.queryKey[0] === "pipeline" && query.queryKey[1] === "brief") });
  const lists = queryClient.getQueriesData<ListData>({ predicate: (query) => isBriefList(query.queryKey) });
  const briefs = queryClient
    .getQueriesData<BriefDetailResponse>({ queryKey: ["pipeline", "brief"] })
    .filter(([, data]) => data?.brief.id === briefId);
  for (const [key, data] of lists) {
    queryClient.setQueryData<ListData>(key, mapRows(data, (row) => (row.id === briefId ? { ...row, stage: to } : row)));
  }
  for (const [key, data] of briefs) {
    if (data) queryClient.setQueryData<BriefDetailResponse>(key, { ...data, brief: { ...data.brief, stage: to } });
  }
  return {
    restore: () => {
      for (const [key, data] of [...lists, ...briefs]) queryClient.setQueryData(key, data);
    },
  };
}

/** The server's card for a moved concept, in every cached list that holds it. */
function placeInLists(queryClient: QueryClient, card: BriefSummary): void {
  for (const [key, data] of queryClient.getQueriesData<ListData>({ predicate: (query) => isBriefList(query.queryKey) })) {
    queryClient.setQueryData<ListData>(key, mapRows(data, (row) => (row.id === card.id ? { ...row, ...card } : row)));
  }
}

/* ---------------------------------------------------------------- brief chat */

/**
 * The brief chat as the screen holds it: every message oldest first, and the
 * cursor the next poll hands back as `since`.
 */
export interface BriefChat {
  comments: BriefComment[];
  cursor: string | null;
}

type ChatBrief = { id: number; ref: string; stage: PipelineStage };

/**
 * Keyed by stage as well as by brief. The revision message is written inside
 * the transition's own transaction, the longest write into the thread, so when
 * the stage moves the thread is read whole again instead of trusted to a
 * cursor - the API's own advice, and a reload of a few dozen rows.
 */
export const briefChatKey = (briefId: number, stage: PipelineStage) =>
  ["pipeline", "comments", briefId, stage] as const;

/** Reactions in flight, so a poll that lands mid-tap does not undo what the reader just did. */
const reactKey = (briefId: number) => ["pipeline", "comments", briefId, "react"] as const;

const chatTime = (comment: BriefComment) => Date.parse(comment.createdAt);

function sortChat(comments: BriefComment[]): BriefComment[] {
  return comments.sort((a, b) => chatTime(a) - chatTime(b) || a.id - b.id);
}

function sameReactions(a: BriefCommentReaction[], b: BriefCommentReaction[]): boolean {
  return (
    a.length === b.length &&
    a.every((r, i) => {
      const o = b[i]!;
      return r.emoji === o.emoji && r.count === o.count && r.mine === o.mine && r.names.join("\n") === o.names.join("\n");
    })
  );
}

/**
 * One answer folded into the thread.
 *
 * A full read replaces it. A `since` read is merged by id - a change can
 * arrive twice, because the cursor is set early on purpose - and its
 * `reactions` list is the whole truth about reactions on live comments: taking
 * a reaction back leaves no timestamp, so a comment missing from that list has
 * none. Comments with a reaction of the reader's still in flight keep the one
 * on screen until the server answers that request itself.
 */
function mergeBriefChat(
  base: BriefChat | undefined,
  answer: BriefCommentsResponse,
  since: string | null,
  holding: ReadonlySet<number>,
): BriefChat {
  if (!since || !base) return { comments: sortChat([...answer.comments]), cursor: answer.now };

  const byId = new Map(base.comments.map((comment) => [comment.id, comment]));
  for (const comment of answer.comments) {
    const previous = byId.get(comment.id);
    byId.set(comment.id, previous && holding.has(comment.id) ? { ...comment, reactions: previous.reactions } : comment);
  }
  for (const id of answer.deleted) {
    const previous = byId.get(id);
    if (previous && !previous.deletedAt) {
      byId.set(id, { ...previous, bodyMd: "", mentions: [], reactions: [], deletedAt: answer.now });
    }
  }
  if (answer.reactions) {
    const current = new Map(answer.reactions.map((entry) => [entry.commentId, entry.reactions]));
    for (const [id, comment] of byId) {
      if (comment.deletedAt || holding.has(id)) continue;
      const next = current.get(id) ?? [];
      if (!sameReactions(comment.reactions, next)) byId.set(id, { ...comment, reactions: next });
    }
  }
  return { comments: sortChat([...byId.values()]), cursor: answer.now };
}

/** The reader's reaction switched on or off, as the server is about to record it. */
function toggleReaction(
  reactions: BriefCommentReaction[],
  emoji: string,
  on: boolean,
  me: string,
): BriefCommentReaction[] {
  const at = reactions.findIndex((r) => r.emoji === emoji);
  const found = at >= 0 ? reactions[at]! : null;
  if (on) {
    if (found?.mine) return reactions;
    if (!found) return [...reactions, { emoji, count: 1, mine: true, names: [me] }];
    return reactions.map((r, i) => (i === at ? { ...r, count: r.count + 1, mine: true, names: [...r.names, me] } : r));
  }
  if (!found?.mine) return reactions;
  if (found.count <= 1) return reactions.filter((_, i) => i !== at);
  const mine = found.names.lastIndexOf(me);
  return reactions.map((r, i) =>
    i === at
      ? { ...r, count: r.count - 1, mine: false, names: mine >= 0 ? r.names.filter((_, n) => n !== mine) : r.names }
      : r,
  );
}

/**
 * The thread, kept fresh while it is on screen.
 *
 * Polled every 15 seconds and on window focus: a conversation between two
 * people at their desks has to show the other side's answer without a reload,
 * and a 15-second poll of "what changed since" is a few hundred bytes. The
 * first read is the whole thread; every read after it asks only for what
 * changed and folds it in (`mergeBriefChat`). The thread the brief detail
 * already carries is drawn while that first read is on its way.
 */
export function useBriefChat(brief: ChatBrief & { comments?: BriefComment[] }) {
  const queryClient = useQueryClient();
  const key = briefChatKey(brief.id, brief.stage);
  const fallback = brief.comments;

  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const since = queryClient.getQueryData<BriefChat>(key)?.cursor ?? null;
      const answer = await api.pipeline.comments(brief.id, since);
      // Merged onto the thread as it is now, not as it was when the request
      // left: a message sent or a reaction tapped meanwhile is already in it.
      const holding = new Set(
        queryClient
          .getMutationCache()
          .findAll({ mutationKey: reactKey(brief.id), status: "pending" })
          .map((mutation) => (mutation.state.variables as { commentId: number } | undefined)?.commentId)
          .filter((id): id is number => typeof id === "number"),
      );
      return mergeBriefChat(queryClient.getQueryData<BriefChat>(key), answer, since, holding);
    },
    placeholderData: (previous) => previous ?? (fallback ? { comments: fallback, cursor: null } : undefined),
    refetchInterval: 15 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
}

/** Put one comment as the server answered it into the thread, wherever it belongs. */
function useAdoptComment(brief: ChatBrief) {
  const queryClient = useQueryClient();
  return useCallback(
    (comment: BriefComment) => {
      queryClient.setQueryData<BriefChat>(briefChatKey(brief.id, brief.stage), (current) => {
        if (!current) return current;
        const others = current.comments.filter((c) => c.id !== comment.id);
        return { ...current, comments: sortChat([...others, comment]) };
      });
    },
    [queryClient, brief.id, brief.stage],
  );
}

/**
 * A new message, or a reply (`parentId`). No success toast: the message
 * appearing in the thread is the confirmation. The rest of the pipeline is
 * refreshed because the brief's comment count and the bell's other side move.
 */
export function usePostBriefComment(brief: ChatBrief) {
  const onError = useFailureToast();
  const queryClient = useQueryClient();
  const adopt = useAdoptComment(brief);

  return useMutation({
    mutationFn: (args: { bodyMd: string; parentId?: number | null }) =>
      api.pipeline.comment(brief.id, args.bodyMd, args.parentId ?? null),
    onSuccess: (comment) => {
      adopt(comment);
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.brief(brief.ref) });
    },
    onError,
  });
}

/** The author rewrites their message; the server keeps the earlier body for the history. */
export function useEditBriefComment(brief: ChatBrief) {
  const onError = useFailureToast();
  const adopt = useAdoptComment(brief);

  return useMutation({
    mutationFn: (args: { commentId: number; bodyMd: string }) =>
      api.pipeline.editComment(brief.id, args.commentId, args.bodyMd),
    onSuccess: adopt,
    onError,
  });
}

/** Soft: the message stays in place, emptied, so the replies under it still read. */
export function useDeleteBriefComment(brief: ChatBrief) {
  const onError = useFailureToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: number) => api.pipeline.deleteComment(brief.id, commentId),
    onSuccess: (_answer, commentId) => {
      queryClient.setQueryData<BriefChat>(briefChatKey(brief.id, brief.stage), (current) =>
        current
          ? {
              ...current,
              comments: current.comments.map((c) =>
                c.id === commentId
                  ? { ...c, bodyMd: "", mentions: [], reactions: [], deletedAt: new Date().toISOString() }
                  : c,
              ),
            }
          : current,
      );
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.brief(brief.ref) });
    },
    onError,
  });
}

/**
 * One emoji on or off, optimistically: the chip changes on the tap, goes back with
 * a toast if the server refuses, and is replaced by the server's own count
 * when it answers. A reaction is the one write in the chat that has to feel
 * instant, or nobody bothers with it.
 */
export function useReactToBriefComment(brief: ChatBrief) {
  const onError = useFailureToast();
  const queryClient = useQueryClient();
  const adopt = useAdoptComment(brief);
  const { displayName } = useSession();
  const key = briefChatKey(brief.id, brief.stage);

  return useMutation({
    mutationKey: reactKey(brief.id),
    mutationFn: (args: { commentId: number; emoji: string; on: boolean }) =>
      api.pipeline.reactToComment(brief.id, args.commentId, args.emoji, args.on),
    onMutate: async (args) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BriefChat>(key);
      const me = displayName ?? "";
      queryClient.setQueryData<BriefChat>(key, (current) =>
        current
          ? {
              ...current,
              comments: current.comments.map((c) =>
                c.id === args.commentId ? { ...c, reactions: toggleReaction(c.reactions, args.emoji, args.on, me) } : c,
              ),
            }
          : current,
      );
      return { previous };
    },
    onError: (error, _args, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
      onError(error);
    },
    onSuccess: adopt,
  });
}

/** The earlier bodies of one edited message, oldest first; read when somebody opens them. */
export function useBriefCommentHistory(briefId: number, comment: { id: number; editCount: number } | null) {
  return useQuery({
    queryKey: ["pipeline", "comments", briefId, "history", comment?.id ?? 0, comment?.editCount ?? 0] as const,
    queryFn: () => api.pipeline.commentHistory(briefId, comment!.id),
    enabled: comment !== null,
    staleTime: 60 * 1000,
  });
}

/* ------------------------------------------------------------- notifications */

/**
 * The bell.
 *
 * Polled rather than pushed: one small request a minute is cheaper than a
 * socket for a team of a dozen, and the number only has to be right within a
 * minute for "did Gabriela answer yet?" to be a useful question to glance at.
 */
export function useNotifications() {
  const { hasAccess, canUsePipeline } = useSession();
  return useQuery({
    queryKey: pipelineKeys.notifications(),
    queryFn: () => api.pipeline.notifications({ limit: 20 }),
    // A results-only account has no pipeline, so no bell to poll.
    enabled: hasAccess && canUsePipeline,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useMarkNotificationsRead() {
  const onError = useFailureToast();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (ids?: number[]) => api.pipeline.markRead(ids),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.notifications() });
    },
    onError,
  });
  const { mutate } = mutation;
  const markRead = useCallback((ids?: number[]) => mutate(ids), [mutate]);
  return { markRead, isPending: mutation.isPending };
}

/* ----------------------------------------------------------------- the lens */

/**
 * Who the reader is, in the shape the shared rules ask for.
 *
 * `editorId` and `strategistId` are both here because one person can be both -
 * Gabriela writes briefs and produces some of them herself - so neither is
 * derivable from the role. Memoised: it feeds the guards below, and a fresh
 * object every render would recompute them on every keystroke elsewhere.
 */
export function usePipelineViewer(): PipelineViewer {
  // `editor` and `strategist` rather than `user.*`: an admin viewing as an
  // editor who has no account yet still needs that editor's lens.
  const { role, user, editor, strategist, session } = useSession();
  const editorId = editor?.id ?? null;
  const strategistId = strategist ?? null;
  const userId = user?.id ?? null;
  // The person, not the account: a concept her other Google account owns is hers too.
  const accountKey = (session?.strategistAccountIds ?? []).join(",");
  const strategistRowId = session?.strategistRowId ?? null;
  return useMemo(
    () => ({
      role,
      userId,
      editorId,
      strategistId,
      accountIds: accountKey ? accountKey.split(",").map(Number) : undefined,
      strategistRowId,
    }),
    [role, userId, editorId, strategistId, accountKey, strategistRowId],
  );
}

/* ------------------------------------------------------------ what to offer */

/**
 * A brief detail as the shared machine wants to see it.
 *
 * The page's buttons and an open note dialog ask the same question of the same
 * brief - may this move be made from where it stands - so they build the
 * question the same way; the server checks it again.
 */
export function transitionGuardOf(brief: BriefDetail): TransitionBrief {
  return {
    stage: brief.stage,
    editorId: brief.editorId,
    strategistUserId: brief.strategistUserId,
    strategistId: brief.strategistId,
    createdByUserId: brief.createdByUserId,
    dueOn: brief.dueOn,
    deliveryUrl: brief.deliveryUrl,
    // The brief's own hooks and script, or its presentation's - as the server counts them.
    realHookCount: brief.hookCount + (brief.storyboard?.hookCount ?? 0),
    hasScript: (brief.script?.scriptMd ?? "").trim().length > 0 || (brief.storyboard?.hasScript ?? false),
  };
}

/**
 * Which delivery a brief is on: the folder the work went into, when it was
 * last handed in, and the revision round it answers.
 *
 * A revision note is about one delivery. When the editor steps a delivery back
 * and hands it in again while somebody is writing about it, the brief is back
 * in the same stage and only these say it is not the same work; an open note
 * dialog compares them with the ones it opened on. Read off the event log, the
 * last forward move into `in_review` - "take back from the admin" lands there
 * too, and is not a delivery.
 */
export interface DeliveryMark {
  url: string | null;
  at: string | null;
  round: number | null;
}

export function deliveryMarkOf(brief: BriefDetail): DeliveryMark {
  let at: string | null = null;
  for (const event of brief.events) {
    if (event.toStage === "in_review" && event.fromStage === "in_progress") at = event.at;
  }
  return { url: brief.deliveryUrl, at, round: brief.revisionRound ?? null };
}

/**
 * Which moves to draw buttons for.
 *
 * The server answers this too, and the client re-derives it with the same
 * predicate and takes the union, so a button is never missing because one
 * side's copy of the brief is a moment stale. The one stand-in is the note:
 * "say why" is collected by a dialog, so a move whose only obstacle is the
 * note stays offerable. The delivery folder gets no stand-in - it is on the
 * brief or it is not, and the page draws "Entregar" greyed out with the reason
 * (see `deliveryBlockedFor`). Nothing is granted by this: every move is
 * checked again, for real, when the request arrives.
 */
export function offerableTransitions(
  brief: TransitionBrief,
  viewer: PipelineViewer,
  fromServer: PipelineStage[],
): PipelineStage[] {
  const stand = { note: "?" };
  const local = nextStages(brief.stage).filter(
    (to) => canTransition(brief.stage, to, viewer, brief, stand).ok,
  );
  return nextStages(brief.stage).filter((to) => fromServer.includes(to) || local.includes(to));
}

/**
 * Whether this viewer could deliver this brief if only it had a folder.
 *
 * True on exactly one brief shape: in progress, no delivery folder, and a
 * viewer the machine would otherwise let deliver it. The page then draws the
 * deliver button disabled with the reason, rather than drawing nothing - a
 * missing button reads as a bug, a greyed one reads as "not yet".
 */
export function deliveryBlockedFor(brief: TransitionBrief, viewer: PipelineViewer): boolean {
  if (brief.stage !== "in_progress" || hasDeliveryFolder(brief)) return false;
  return canTransition("in_progress", "in_review", viewer, { ...brief, deliveryUrl: "?" }).ok;
}

/**
 * Why this viewer cannot release this concept to its editor yet, or null.
 *
 * Null when the move is open, and null when it would never be this viewer's to
 * make (another strategist's concept, an editor) - a missing button is right
 * there. Otherwise the first thing the shared guard asks for: an editor, then
 * a real hook, then a script. The page draws "Ready for editors" greyed out
 * with that reason beside it, because "pick an editor first" is a state, and a
 * button that is simply missing reads as a bug.
 */
export function releaseBlockedFor(brief: TransitionBrief, viewer: PipelineViewer): TransitionRefusal | null {
  if (brief.stage !== "strategist_review") return null;
  const ready = { ...brief, editorId: brief.editorId ?? -1, realHookCount: Math.max(brief.realHookCount, 1), hasScript: true };
  if (!canTransition("strategist_review", "assigned", viewer, ready).ok) return null;
  const check = canTransition("strategist_review", "assigned", viewer, brief);
  return check.ok ? null : check.reason;
}

/* ---------------------------------------------------------------- authoring */

/**
 * The composer's pickers.
 *
 * One request, cached for the session: products, frameworks, techniques,
 * angles, the strategists and - the part that matters - every editor with the
 * number of briefs already on their plate. "Who is free" is the question being
 * asked at the moment of assignment, and a list of names does not answer it.
 */
export function usePipelineLookups(options: { enabled?: boolean } = {}) {
  return useQuery({ ...lookupsQuery(), enabled: options.enabled ?? true });
}

/**
 * Say where the video goes.
 *
 * The same patch endpoint as the composer, with one field, and loud where the
 * composer is quiet: this is a deliberate act on somebody else's page rather
 * than an autosave, and the strategist wants to hear that it landed. The whole
 * pipeline is invalidated because the folder is on the summary too, and the
 * editor's bell has a new row.
 */
export function useSetDeliveryFolder(brief: { id: number; ref: string }) {
  const { t } = useTranslation();
  const onError = useFailureToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      deliveryUrl,
      rowVersion,
    }: {
      deliveryUrl: string | null;
      /** What was there before, so the toast can say "changed" rather than "assigned". */
      previous: string | null;
      rowVersion: number | null;
    }) => api.pipeline.patch(brief.id, { deliveryUrl }, rowVersion),
    onSuccess: (_result, { deliveryUrl, previous }) => {
      void queryClient.invalidateQueries({ queryKey: pipelineKey });
      const key =
        deliveryUrl === null
          ? "pipeline.editor.folder.toasts.removed"
          : previous
            ? "pipeline.editor.folder.toasts.changed"
            : "pipeline.editor.folder.toasts.assigned";
      toast.success(t(key, { ref: brief.ref }));
    },
    onError: (error) => {
      onError(error);
      if (isStale(error)) void queryClient.invalidateQueries({ queryKey: pipelineKeys.brief(brief.ref) });
    },
  });
}

/**
 * Choose, change or remove the editor from a concept's page. Loud, like the
 * folder: a deliberate act the strategist wants to hear landed. A refusal
 * because the concept moved on re-reads it, so the control locks itself.
 */
export function useSetBriefEditor(brief: { id: number; ref: string }) {
  const { t } = useTranslation();
  const onError = useFailureToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      editorId,
    }: {
      editorId: number | null;
      /** The new editor's name, for the toast. */
      name: string | null;
    }) => api.pipeline.assign(brief.id, editorId),
    onSuccess: (result, { name }) => {
      void queryClient.invalidateQueries({ queryKey: pipelineKey });
      if (!result.changed) return;
      toast.success(
        result.editorId === null
          ? t("pipeline.people.toasts.removed", { ref: brief.ref })
          : result.previousEditorId === null
            ? t("pipeline.people.toasts.assigned", { ref: brief.ref, name: name ?? "" })
            : t("pipeline.people.toasts.changed", { ref: brief.ref, name: name ?? "" }),
      );
    },
    onError: (error) => {
      onError(error);
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.brief(brief.ref) });
    },
  });
}

/** Admins: name the concept's strategist, or null for any strategist. */
export function useSetBriefStrategist(brief: { id: number; ref: string }) {
  const { t } = useTranslation();
  const onError = useFailureToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ strategistId }: { strategistId: number | null; /** For the toast. */ name: string | null }) =>
      api.pipeline.setStrategist(brief.id, strategistId),
    onSuccess: (result, { name }) => {
      void queryClient.invalidateQueries({ queryKey: pipelineKey });
      if (!result.changed) return;
      toast.success(
        result.strategistId === null
          ? t("pipeline.people.toasts.anyStrategist", { ref: brief.ref })
          : t("pipeline.people.toasts.strategist", { ref: brief.ref, name: name ?? "" }),
      );
    },
    onError: (error) => {
      onError(error);
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.brief(brief.ref) });
    },
  });
}

/** Strategists and admins: what kind of concept it is, or null for none. */
export function useSetBriefCategory(brief: { id: number; ref: string }) {
  const { t } = useTranslation();
  const onError = useFailureToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ category }: { category: string | null }) => api.pipeline.setCategory(brief.id, category),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: pipelineKey });
      if (!result.changed) return;
      toast.success(
        result.category === null
          ? t("naming.category.toasts.removed", { ref: brief.ref })
          : t("naming.category.toasts.set", { ref: brief.ref, category: result.category.label }),
      );
    },
    onError: (error) => {
      onError(error);
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.brief(brief.ref) });
    },
  });
}
